import { nanoid } from "nanoid";
import { MAX_FILE_SIZE } from "../../constants";
import { getMimeTypeFromFilename, isTextMimeType } from "../../lib";
import type { AttachmentFile } from "../state/stores/types";
import { formatFileSize } from "./utils";

// Constants
const TEXT_SAMPLE_SIZE = 1000;

/**
 * Filters a FileList to only include supported file types
 */
export function filterSupportedFiles(fileList: FileList): File[] {
  return Array.from(fileList).filter(isFileSupported);
}

/**
 * Processes files into attachment objects with size validation and sequential processing
 * to avoid memory issues with large files or many files
 */
export async function processFilesToAttachments(
  files: File[],
): Promise<AttachmentFile[]> {
  const processedFiles: AttachmentFile[] = [];

  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      console.warn(
        `File ${file.name} is too large (${formatFileSize(file.size)}) and will be skipped. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`,
      );
      continue;
    }

    try {
      const content = await file.text();

      // Validate that the content is text-like (not binary)
      if (!isContentTextLike(content)) {
        console.warn(
          `File ${file.name} appears to contain binary data and will be skipped. Only text files are supported.`,
        );
        continue;
      }

      processedFiles.push({
        id: nanoid(),
        name: file.name,
        content,
        size: file.size,
        lastModified: file.lastModified,
      });
    } catch (error) {
      console.error(`Failed to read file ${file.name}:`, error);
    }
  }

  return processedFiles;
}

/**
 * Checks if a file is supported (text-based) using custom MIME type detection
 */
function isFileSupported(file: File): boolean {
  // If the browser provided a MIME type, check if it's text-based
  if (file.type) {
    return isTextMimeType(file.type);
  }

  // If no MIME type from browser, try to determine from filename
  const detectedMimeType = getMimeTypeFromFilename(file.name);
  if (detectedMimeType) {
    return isTextMimeType(detectedMimeType);
  }

  // If we can't determine the MIME type, allow it and let content validation handle it
  // This handles cases like files without extensions (README, Makefile, etc.)
  return true;
}

/**
 * Validates that content appears to be text-based rather than binary
 * Uses heuristics to detect binary content by checking for null bytes and non-printable characters
 */
function isContentTextLike(content: string): boolean {
  // Empty content is considered text
  if (content.length === 0) {
    return true;
  }

  // Check for null bytes, which are a strong indicator of binary content
  if (content.includes("\0")) {
    return false;
  }

  // Sample first TEXT_SAMPLE_SIZE characters for performance on large files
  const sampleSize = Math.min(TEXT_SAMPLE_SIZE, content.length);
  const sample = content.slice(0, sampleSize);

  let nonPrintableCount = 0;
  let totalChars = 0;

  for (const char of sample) {
    totalChars++;
    const charCode = char.charCodeAt(0);

    // Count non-printable characters (excluding common whitespace)
    if (charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13) {
      nonPrintableCount++;
    }
  }

  // If more than 5% of characters are non-printable, consider it binary
  const nonPrintableRatio = nonPrintableCount / totalChars;
  return nonPrintableRatio <= 0.05;
}

/**
 * Shared utility to process files and add them to the attachment store
 * Handles filtering supported files, processing them to attachments, and adding to store
 * Returns information about the processing results for user feedback
 */
export async function processAndAddFiles(
  fileList: FileList,
  addFiles: (files: AttachmentFile[]) => void,
): Promise<{
  totalFiles: number;
  supportedFiles: number;
  processedFiles: number;
  rejectedFiles: number;
}> {
  const allFiles = Array.from(fileList);
  const supportedFiles = filterSupportedFiles(fileList);
  const rejectedCount = allFiles.length - supportedFiles.length;

  if (supportedFiles.length === 0) {
    return {
      totalFiles: allFiles.length,
      supportedFiles: 0,
      processedFiles: 0,
      rejectedFiles: rejectedCount,
    };
  }

  const processedFiles = await processFilesToAttachments(supportedFiles);
  addFiles(processedFiles);

  return {
    totalFiles: allFiles.length,
    supportedFiles: supportedFiles.length,
    processedFiles: processedFiles.length,
    rejectedFiles:
      rejectedCount + (supportedFiles.length - processedFiles.length),
  };
}
